import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { embeddedSignupService } from '../services/api';

const ALLOWED_HTTP_HOSTS = ['localhost', '127.0.0.1'];
const DEFAULT_META_APP_ID = '690735040099659'; // PRODUCTION
// const DEFAULT_META_APP_ID = '1344386363460441'; // STAGING

const DEFAULT_META_CONFIG_ID = '1563935541405382';  // PRODUCTION
// const DEFAULT_META_CONFIG_ID = '1748883592397733';  // STAGING


const ALLOWED_CLIDS = ['DEMO0010', 'DEMO0011', 'SGCL0003'];

const EmbeddedSignup = () => {
  const { t } = useLanguage();
  const { clid } = useAuth();
  const [sdkReady, setSdkReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeStatus, setCodeStatus] = useState('idle'); // idle | processing | success | error
  const [exchangeResult, setExchangeResult] = useState(null);
  const [processingError, setProcessingError] = useState(null);
  const [capturedCode, setCapturedCode] = useState(null); // Store captured code for display
  const [onboardingSaved, setOnboardingSaved] = useState(false);
  const fbLoginCodeRef = useRef(null); // Store code from FB.login response
  const hasProcessedRef = useRef(false); // Prevent duplicate processing
  const exchangeResultRef = useRef(null); // Store exchangeResult to avoid re-registering event listener
  const onboardingSavedRef = useRef(false); // Prevent duplicate save calls
  const sessionWabaIdRef = useRef(null);
  const sessionPhoneNumberIdRef = useRef(null);
  const messageDebugRef = useRef({
    lastObservedLogAtMs: 0,
    seenIgnoredOrigins: new Set(),
    loggedJsonParseFailure: false,
  });

  const appId =
    process.env.META_WABA_APP_ID || DEFAULT_META_APP_ID;
  const configId =
    process.env.META_WABA_APP_CONFIG_ID ||
    DEFAULT_META_CONFIG_ID;

  const loadFacebookSdk = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const initializeSdk = () => {
      if (!window.FB) {
        console.warn('EmbeddedSignup: FB object missing during init');
        return;
      }
      window.FB.init({
        appId: appId,
        cookie: true,
        xfbml: true,
        version: 'v20.0',
      });
      setSdkReady(true);
      window.dispatchEvent(new Event('fb-sdk-ready'));
    };

    if (window.FB) {
      initializeSdk();
      return;
    }

    window.fbAsyncInit = initializeSdk;
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      document.body.appendChild(script);
    }
  }, [appId]);

  // Immediate debug log on component mount - runs once
  useEffect(() => {
    console.warn('🔍 EMBEDDED SIGNUP DEBUG - Component Mounted');
    console.warn('🔍 Hostname:', window.location.hostname);
    console.warn('🔍 Origin:', window.location.origin);
    console.warn('🔍 Full URL:', window.location.href);
    console.warn('🔍 Protocol:', window.location.protocol);
    console.warn('🔍 CLID:', clid);
    console.warn('🔍 App ID:', appId);
  }, []); // Empty deps = runs once on mount

  useEffect(() => {
    // Debug: Log hostname and origin for Facebook SDK domain validation
    console.log('EmbeddedSignup: Hostname:', window.location.hostname);
    console.log('EmbeddedSignup: Origin:', window.location.origin);
    console.log('EmbeddedSignup: Full URL:', window.location.href);
    console.log('EmbeddedSignup: Protocol:', window.location.protocol);

    loadFacebookSdk();
  }, [loadFacebookSdk]);

  // Keep exchangeResultRef in sync with exchangeResult
  useEffect(() => {
    exchangeResultRef.current = exchangeResult;
  }, [exchangeResult]);

  const maybeSaveOnboardingRecord = useCallback(
    async (result) => {
      if (onboardingSavedRef.current) return;

      const waba_id = sessionWabaIdRef.current;
      const phone_number_id = sessionPhoneNumberIdRef.current;
      const access_token = result?.access_token;
      const expires_in = result?.expires_in;

      if (!clid || !waba_id || !phone_number_id || !access_token) {
        return;
      }

      try {
        onboardingSavedRef.current = true;
        console.info('EmbeddedSignup: Saving onboarding record...', {
          clid,
          waba_id,
          phone_number_id,
          hasAccessToken: !!access_token,
          expires_in,
        });

        await embeddedSignupService.saveOnboardingRecord({
          waba_id,
          phone_number_id,
          access_token,
          expires_in,
        });

        setOnboardingSaved(true);
        toast.success('✓ Onboarding record saved');
      } catch (error) {
        onboardingSavedRef.current = false;
        console.error('EmbeddedSignup: Failed to save onboarding record', {
          error: error.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
        toast.error('Failed to save onboarding record');
      }
    },
    [clid, t]
  );

  // Listen for session events from Meta Embedded Signup
  useEffect(() => {
    const handleMessage = (event) => {
      // Throttled logging so we can debug why session events aren't detected (without spamming).
      try {
        const now = Date.now();
        if (now - (messageDebugRef.current?.lastObservedLogAtMs || 0) > 1000) {
          messageDebugRef.current.lastObservedLogAtMs = now;
          console.info('EmbeddedSignup: message event observed', {
            origin: event?.origin,
            dataType: typeof event?.data,
            dataPreview:
              typeof event?.data === 'string'
                ? event.data.slice(0, 200)
                : event?.data,
          });
        }
      } catch {
        // ignore logging failures
      }

      // Match Meta sample patterns: validate origin and parse message data
      if (event?.origin !== 'https://www.facebook.com') {
        try {
          const origin = event?.origin || '(missing origin)';
          if (!messageDebugRef.current.seenIgnoredOrigins.has(origin)) {
            messageDebugRef.current.seenIgnoredOrigins.add(origin);
            console.info('EmbeddedSignup: message ignored due to origin mismatch', {
              origin,
              expectedOrigin: 'https://www.facebook.com',
            });
          }
        } catch {
          // ignore logging failures
        }
        return;
      }

      let data = null;
      if (typeof event.data === 'string') {
        try {
          data = JSON.parse(event.data);
        } catch {
          // Not a JSON payload; ignore.
          try {
            if (!messageDebugRef.current.loggedJsonParseFailure) {
              messageDebugRef.current.loggedJsonParseFailure = true;
              console.info('EmbeddedSignup: message ignored due to JSON parse failure', {
                origin: event?.origin,
                dataPreview: event?.data?.slice?.(0, 200),
              });
            }
          } catch {
            // ignore logging failures
          }
          return;
        }
      } else if (event.data && typeof event.data === 'object') {
        data = event.data;
      } else {
        return;
      }

      if (!data || data.type !== 'WA_EMBEDDED_SIGNUP') {
        return;
      }

      console.info('EmbeddedSignup: Session event received', {
        origin: event.origin,
        dataType: typeof event.data,
        type: data.type,
        event: data.event,
        version: data.version,
        data: data.data,
      });

      const isFinish =
        data.event === 'FINISH' ||
        data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING';

      if (isFinish) {
        const wabaId = data.data?.waba_id;
        const phoneNumberId = data.data?.phone_number_id;

        if (wabaId) {
          sessionWabaIdRef.current = wabaId;
        }
        if (phoneNumberId) {
          sessionPhoneNumberIdRef.current = phoneNumberId;
        }

        if (wabaId) {
          console.info('EmbeddedSignup: waba_id received from session event', {
            wabaId,
            phoneNumberId,
            event: data.event,
            timestamp: new Date().toISOString(),
          });

          // Log additional context using ref to avoid dependency
          console.info('EmbeddedSignup: Session event waba_id details', {
            wabaId,
            hasExchangeResult: !!exchangeResultRef.current,
            currentWabaId: exchangeResultRef.current?.waba_id,
            clid,
            isWabaIdDifferent: exchangeResultRef.current?.waba_id !== wabaId,
          });

          // Update UI state so Latest Result shows the real waba_id when available.
          setExchangeResult((prev) => {
            if (!prev) return prev;
            if (prev?.waba_id === wabaId) return prev;

            return {
              ...prev,
              waba_id_from_oauth_exchange: prev?.waba_id,
              waba_id_from_session_event: wabaId,
              waba_id: wabaId,
            };
          });

          // If token exchange already completed, persist immediately.
          if (exchangeResultRef.current?.access_token) {
            void maybeSaveOnboardingRecord(exchangeResultRef.current);
          }
        } else {
          console.warn('EmbeddedSignup: Finish session event received but no waba_id in data', {
            event: data.event,
            data: data.data,
          });
        }
      } else {
        console.info('EmbeddedSignup: Non-finish embedded signup session event received', {
          event: data.event,
          data: data.data,
        });
      }
    };

    window.addEventListener('message', handleMessage);
    console.info('EmbeddedSignup: Session event listener registered');

    return () => {
      window.removeEventListener('message', handleMessage);
      console.info('EmbeddedSignup: Session event listener removed');
    };
  }, [clid, maybeSaveOnboardingRecord]);

  // Initial check: If page loads with code in URL, capture it immediately
  useEffect(() => {
    console.info('EmbeddedSignup: Component mounted, checking initial state', {
      currentUrl: window.location.href,
      hasCodeInUrl: window.location.search.includes('code'),
    });

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      console.info('EmbeddedSignup: Code found in URL on initial load', {
        codeLength: code.length,
        codePreview: `${code.substring(0, 20)}...`,
      });
      console.info('EmbeddedSignup: Initial load code capture', {
        currentUrl: window.location.href,
      });
      setCapturedCode(code);
      fbLoginCodeRef.current = code;
    }
  }, []);

  const exchangeAuthorizationCode = useCallback(
    async (code, source = 'unknown') => {
      console.info(`EmbeddedSignup: exchangeAuthorizationCode called from ${source}`, {
        code: code ? `${code.substring(0, 20)}...` : 'null',
        hasProcessed: hasProcessedRef.current,
      });

      if (hasProcessedRef.current) {
        console.info('EmbeddedSignup: Code exchange already processed, skipping duplicate call');
        return;
      }

      if (!code) {
        console.error('EmbeddedSignup: No code provided to exchangeAuthorizationCode');
        return;
      }

      try {
        console.info('EmbeddedSignup: Starting code exchange process...');
        hasProcessedRef.current = true;
        setCodeStatus('processing');
        setProcessingError(null);

        // **************************************************
        // Step 5: Exchange authorization code （When the user completes the login dialog flow we will redirect the user to your redirect URL and include a code. You must then exchange this code for an access token）
        // **************************************************
        console.info('EmbeddedSignup: Calling embeddedSignupService.exchangeAuthorizationCode...', {
          codeLength: code.length,
          source: source,
          clid: clid,
        });
        let result = await embeddedSignupService.exchangeAuthorizationCode(code);

        // Please check, should be like this:
        console.info('EmbeddedSignup: Code exchange successful!', {
          hasAccessToken: !!result?.access_token,
          accessToken: result?.access_token,
          accessTokenLength: result?.access_token?.length,
          expiresIn: result?.expires_in,
        });

        setExchangeResult(result);

        // **************************************************
        // Step 6: After get the access token, we need to save the access token, waba_id, phone_number_id to the database
        // **************************************************

        void maybeSaveOnboardingRecord(result);
        setCodeStatus('success');
        toast.success(t('embeddedSignup.success.exchange'));
      } catch (error) {
        console.error('EmbeddedSignup: Code exchange failed:', {
          error: error.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
        setProcessingError(error?.response?.data?.detail || error.message);
        setCodeStatus('error');
        toast.error(t('embeddedSignup.error.exchange'));
        hasProcessedRef.current = false; // Allow retry on error
      } finally {
        clearCodeQueryParam();
      }
    },
    [maybeSaveOnboardingRecord, t]
  );

  const clearCodeQueryParam = () => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    if (params.has('code')) {
      console.info('EmbeddedSignup: Clearing code from URL query params');
      params.delete('code');
      const query = params.toString();
      const newPath = query ? `${url.pathname}?${query}` : url.pathname;
      const finalUrl = url.hash ? `${newPath}${url.hash}` : newPath;
      window.history.replaceState({}, document.title, finalUrl);
      console.info('EmbeddedSignup: URL cleaned, code removed from address bar');
    }
  };

  // Fallback: Process code from URL if session event hasn't triggered yet
  useEffect(() => {
    console.info('EmbeddedSignup: Checking for code in URL (fallback mechanism)', {
      currentUrl: window.location.href,
      hasProcessed: hasProcessedRef.current,
    });

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (code) {
      console.info('EmbeddedSignup: Code found in URL', {
        codeLength: code.length,
        codePreview: `${code.substring(0, 20)}...`,
        hasProcessed: hasProcessedRef.current,
      });

      if (!hasProcessedRef.current) {
        // Store code for display
        console.info('EmbeddedSignup: Storing code from URL for UI display', {
          currentUrl: window.location.href,
        });
        setCapturedCode(code);

        // Small delay to allow session event to process first
        console.info('EmbeddedSignup: Setting up fallback timer (500ms delay)');
        const timer = setTimeout(() => {
          if (!hasProcessedRef.current) {
            console.info('EmbeddedSignup: Fallback timer triggered - processing code from URL');
            exchangeAuthorizationCode(code, 'url-fallback');
          } else {
            console.info('EmbeddedSignup: Fallback timer skipped - code already processed');
          }
        }, 500);
        return () => {
          console.info('EmbeddedSignup: Cleaning up fallback timer');
          clearTimeout(timer);
        };
      } else {
        console.info('EmbeddedSignup: Code in URL but already processed, skipping fallback');
      }
    } else {
      console.debug('EmbeddedSignup: No code found in URL');
    }
  }, [exchangeAuthorizationCode]);

  const handleEmbeddedSignup = async () => {
    console.info('EmbeddedSignup: handleEmbeddedSignup called', {
      isSubmitting,
      sdkReady,
      hasFB: typeof window.FB !== 'undefined',
      appId,
      configId,
    });

    if (isSubmitting) {
      console.warn('EmbeddedSignup: Already submitting, ignoring duplicate call');
      return;
    }

    if (!sdkReady || typeof window.FB === 'undefined') {
      console.warn('EmbeddedSignup: SDK not ready', { sdkReady, fbDefined: typeof window.FB });
      toast.error(t('embeddedSignup.error.sdkNotReady'));
      return;
    }

    if (!appId || !configId) {
      console.warn('EmbeddedSignup: Missing Meta config', { appId, configId });
      toast.error(t('embeddedSignup.error.missingConfig'));
      return;
    }

    const isHttps = window.location.protocol === 'https:';
    const isLocalHost = ALLOWED_HTTP_HOSTS.includes(window.location.hostname);
    if (!isHttps && !isLocalHost) {
      console.warn('EmbeddedSignup: HTTPS/localhost requirement failed', {
        protocol: window.location.protocol,
        hostname: window.location.hostname,
      });
      toast.error(t('embeddedSignup.error.httpsRequired'));
      return;
    }

    try {
      console.info('EmbeddedSignup: Launching FB.login with Embedded Signup config', {
        appId,
        configId,
      });
      console.info('EmbeddedSignup: Using config_id for FB.login', {
        configId,
        appId,
        redirectUri: 'https://portal.nexicare.ai/embedded-signup',
      });
      setIsSubmitting(true);
      const extras = {
        setup: {},
        featureType: 'whatsapp_business_app_onboarding',
        sessionInfoVersion: '3',
      };



      // **************************************************
      // Step 2: Customize Embedded Signup
      // **************************************************
      window.FB.login(
        (response) => {
          // Print complete raw response from Meta
          console.group('🔵 FB.login Complete Raw Response from Meta');
          // Method 1: Direct log (shows object structure)
          console.log('Response Object:', response);
          // Method 2: JSON stringify (shows all properties as JSON)
          try {
            console.log('Response as JSON:', JSON.stringify(response, null, 2));
          } catch (e) {
            console.warn('Cannot stringify response (may contain circular refs):', e);
          }
          console.groupEnd();

          console.info('EmbeddedSignup: FB.login callback invoked', {
            hasResponse: !!response,
            hasAuthResponse: !!response?.authResponse,
            status: response?.status,
          });

          setIsSubmitting(false);

          if (!response.authResponse) {
            console.warn('EmbeddedSignup: FB.login cancelled or failed - no authResponse', {
              status: response?.status,
              response: response,
            });
            toast.error(t('embeddedSignup.error.fbCancelled'));
            return;
          }

          // **************************************************
          // Step 4: Onboarding business customers
          // **************************************************

          // From DOC: When a business customer successfully completes the Embedded Signup flow, their asset IDs and an exchangeable token code will be returned to the window that spawned the flow, as normal, but the session event payload will have event set to FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING
          // {
          //     data: {
          //       waba_id: "<CUSTOMER_WABA_ID>"
          //     },
          //     type: "WA_EMBEDDED_SIGNUP",
          //     event: "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
          //     version: 3
          //   }
          // Capture the customer’s asset IDs and exchangeable token code and use them to onboard the customer as you normally would, but skip the phone number registration step, as the number is already registered.


          // Capture the code from the response if available
          const code = response.authResponse?.code;
          console.info('EmbeddedSignup: FB.login callback executed', {
            hasAuthResponse: !!response.authResponse,
            hasCode: !!code,
            codeLength: code?.length,
            codePreview: code ? `${code.substring(0, 20)}...` : 'null',
          });

          if (code) {
            console.info('EmbeddedSignup: Code received from FB.login response', {
              codeLength: code.length,
              codePreview: `${code.substring(0, 20)}...`,
            });

            fbLoginCodeRef.current = code;
            setCapturedCode(code); // Update UI to show the code
            console.info('EmbeddedSignup: Code stored in ref and state for display', {
              currentUrl: window.location.href,
            });

            // Wait a bit for session event to arrive, if not, process the code directly
            console.info('EmbeddedSignup: Setting up FB.login fallback timer (1000ms delay)');
            const fbLoginTimer = setTimeout(() => {
              if (!hasProcessedRef.current) {
                console.info('EmbeddedSignup: FB.login fallback timer triggered - processing code from FB.login response');
                exchangeAuthorizationCode(code, 'fb-login-fallback');
              } else {
                console.info('EmbeddedSignup: FB.login fallback timer skipped - code already processed by session event or URL fallback');
              }
            }, 1000); // Wait 1 second for session event

            // Store timer reference for cleanup if needed
            window._fbLoginTimer = fbLoginTimer;
          } else {
            console.info('EmbeddedSignup: FB.login completed, no code in response - code will come via URL redirect');
          }
        },
        {
          config_id: configId,
          response_type: 'code',
          override_default_response_type: true,
          extras,
        }
      );
    } catch (error) {
      console.error('EmbeddedSignup: Failed to launch FB.login', {
        error: error.message,
        stack: error.stack,
      });
      toast.error(t('embeddedSignup.placeholderError'));
      setIsSubmitting(false);
    } finally {
      // No-op: setIsSubmitting handled in FB callback
    }
  };


  const codeStatusCopy = useMemo(
    () => ({
      idle: t('embeddedSignup.status.codeIdle'),
      processing: t('embeddedSignup.status.codeProcessing'),
      success: t('embeddedSignup.status.codeSuccess'),
      error: t('embeddedSignup.status.codeError'),
    }),
    [t]
  );

  // Only allow access for DEMO0010
  if (!ALLOWED_CLIDS.includes(clid)) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm text-center">
          <h1 className="text-2xl font-bold mb-4 text-gray-700">{t('embeddedSignup.accessRestricted', 'Access Restricted')}</h1>
          <p className="text-gray-600">
            {t('embeddedSignup.accessRestrictedMessage', 'This feature is only available for authorized clinics.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">{t('embeddedSignup.title')}</h1>
      <p className="text-gray-600 mb-6">{t('embeddedSignup.description')}</p>

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm font-semibold text-blue-800 mb-1">{t('embeddedSignup.instruction.step1Title')}</p>
        <p className="text-sm text-blue-700 mb-3">{t('embeddedSignup.instruction.step1Text')}</p>
        <p className="text-sm font-semibold text-blue-800 mb-1">{t('embeddedSignup.instruction.step2Title')}</p>
        <p className="text-sm text-blue-700 mb-3">{t('embeddedSignup.instruction.step2Text')}</p>
        <p className="text-sm font-semibold text-blue-800 mb-1">{t('embeddedSignup.instruction.step3Title')}</p>
        <p className="text-sm text-blue-700">{t('embeddedSignup.instruction.step3Text')}</p>
      </div>

      <button
        onClick={handleEmbeddedSignup}
        disabled={isSubmitting}
        className="inline-flex items-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {isSubmitting ? t('embeddedSignup.button.loading') : t('embeddedSignup.button.label')}
      </button>
      <p className="text-xs text-gray-500 mt-2">
        {t('embeddedSignup.button.caption')}
      </p>

      <div className="mt-8 grid gap-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-gray-700">{t('embeddedSignup.status.sdkTitle')}</p>
          <p className={`text-sm ${sdkReady ? 'text-emerald-600' : 'text-gray-500'}`}>
            {sdkReady ? t('embeddedSignup.status.sdkReady') : t('embeddedSignup.status.sdkLoading')}
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-gray-700">{t('embeddedSignup.status.codeTitle')}</p>
          <p
            className={`text-sm ${
              codeStatus === 'success'
                ? 'text-emerald-600'
                : codeStatus === 'error'
                ? 'text-rose-600'
                : 'text-gray-600'
            }`}
          >
            {codeStatusCopy[codeStatus]}
          </p>
          {processingError && (
            <p className="mt-1 text-xs text-rose-500 break-all">
              {processingError}
            </p>
          )}
          {capturedCode && (
            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-xs font-semibold text-yellow-800 mb-1">Authorization Code:</p>
              <p className="text-xs text-yellow-700 break-all font-mono">{capturedCode}</p>
            </div>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-gray-700">{t('embeddedSignup.status.latestResult')}</p>
          {exchangeResult?.access_token ? (
            <>
              {exchangeResult.waba_id ? (
                <p className="text-sm text-emerald-600">
                  {t('embeddedSignup.status.wabaLabel')} {exchangeResult.waba_id}
                </p>
              ) : (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-xs font-semibold text-yellow-800 mb-1">Warning: No waba_id</p>
                  <p className="text-xs text-yellow-700">
                    OAuth response did not include waba_id.
                  </p>
                </div>
              )}
              {typeof exchangeResult.expires_in !== 'undefined' && exchangeResult.expires_in !== null && (
                <p className="text-xs text-gray-500">
                  Expires in: {exchangeResult.expires_in}s
                </p>
              )}
              {exchangeResult.access_token && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-xs font-semibold text-blue-800 mb-1">Access Token:</p>
                  <p className="text-xs text-blue-700 break-all font-mono">{exchangeResult.access_token}</p>
                </div>
              )}
              <p className={`mt-2 text-xs ${onboardingSaved ? 'text-emerald-600' : 'text-gray-500'}`}>
                {onboardingSaved ? '✓ Onboarding record saved' : 'Onboarding record not saved yet'}
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-500">{t('embeddedSignup.status.noResult')}</p>
          )}
        </div>

      </div>
    </div>
  );
};

export default EmbeddedSignup;

